'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type KanoAnswer = 'L' | 'E' | 'N' | 'T' | 'D';
type KanoCategory = 'P' | 'M' | 'A' | 'I' | 'R' | 'Q';

type Item = {
  id: string;
  name: string;
  functional: KanoAnswer;
  dysfunctional: KanoAnswer;
};

const ANSWER_OPTIONS: { value: KanoAnswer; label: string }[] = [
  { value: 'L', label: 'Like it' },
  { value: 'E', label: 'Expect it' },
  { value: 'N', label: 'Neutral' },
  { value: 'T', label: 'Tolerate it' },
  { value: 'D', label: 'Dislike it' },
];

const MATRIX: Record<KanoAnswer, Record<KanoAnswer, KanoCategory>> = {
  L: { L: 'Q', E: 'A', N: 'A', T: 'A', D: 'P' },
  E: { L: 'R', E: 'I', N: 'I', T: 'I', D: 'M' },
  N: { L: 'R', E: 'I', N: 'I', T: 'I', D: 'M' },
  T: { L: 'R', E: 'I', N: 'I', T: 'I', D: 'M' },
  D: { L: 'R', E: 'R', N: 'R', T: 'R', D: 'Q' },
};

const CATEGORY_META: Record<KanoCategory, { label: string; pillClass: string }> = {
  P: {
    label: 'Performance',
    pillClass: 'bg-primary/15 text-primary',
  },
  M: {
    label: 'Must-be',
    pillClass: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
  A: {
    label: 'Attractive',
    pillClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  I: {
    label: 'Indifferent',
    pillClass: 'bg-muted text-muted-foreground',
  },
  R: {
    label: 'Reverse',
    pillClass: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  },
  Q: {
    label: 'Questionable',
    pillClass: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  },
};

const STORAGE_KEY = 'planetroadmap.kano-tool.v1';

function classify(i: Item): KanoCategory {
  return MATRIX[i.functional][i.dysfunctional];
}

function newItem(name = '', functional: KanoAnswer = 'L', dysfunctional: KanoAnswer = 'D'): Item {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    functional,
    dysfunctional,
  };
}

export function KanoTool() {
  const [items, setItems] = useState<Item[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Item[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setItems([
      newItem('Fast page load', 'L', 'D'),
      newItem('Two-factor authentication', 'E', 'D'),
      newItem('Confetti animation on success', 'L', 'N'),
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [items, hydrated]);

  const enriched = useMemo(
    () => items.map((i) => ({ ...i, category: classify(i) })),
    [items],
  );

  function update(id: string, patch: Partial<Item>) {
    setItems((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  function remove(id: string) {
    setItems((curr) => curr.filter((i) => i.id !== id));
  }

  function add() {
    setItems((curr) => [...curr, newItem()]);
  }

  function reset() {
    if (!confirm('Clear all features and start over?')) return;
    setItems([newItem()]);
  }

  function exportCsv() {
    const labelFor = (a: KanoAnswer) => ANSWER_OPTIONS.find((o) => o.value === a)!.label;
    const rows = [
      ['Feature', 'Functional Answer', 'Dysfunctional Answer', 'Category'],
      ...enriched.map((i) => [
        i.name || 'Untitled',
        labelFor(i.functional),
        labelFor(i.dysfunctional),
        CATEGORY_META[i.category].label,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kano-classification.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading tool…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[220px]">
                  Feature
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  If present, you feel…
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  If absent, you feel…
                </th>
                <th className="text-left py-3 px-4 font-semibold text-primary">Category</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {enriched.map((item, i) => (
                <tr
                  key={item.id}
                  className={i < enriched.length - 1 ? 'border-b border-border/40' : ''}
                >
                  <td className="py-2 px-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => update(item.id, { name: e.target.value })}
                      placeholder="Untitled feature"
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.functional}
                      onChange={(e) =>
                        update(item.id, { functional: e.target.value as KanoAnswer })
                      }
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {ANSWER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.dysfunctional}
                      onChange={(e) =>
                        update(item.id, { dysfunctional: e.target.value as KanoAnswer })
                      }
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {ANSWER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${CATEGORY_META[item.category].pillClass}`}
                    >
                      {CATEGORY_META[item.category].label}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(item.id)}
                      aria-label="Remove feature"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {enriched.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-muted-foreground">
                    No features yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add feature
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={enriched.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Your features are saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}
